/**
 * Time-off page — list requests with status badges, create requests,
 * approve/deny (Manager/Admin), and a leave calendar. PTO balances live in
 * Workforce (not tracked here).
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, Plus, Check, X, Pencil, Trash2, FileDown, Download } from 'lucide-react';
import {
  useTimeOff,
  useCreateTimeOff,
  useUpdateTimeOff,
  useDecideTimeOff,
  useDeleteTimeOff,
} from '../api.js';
import { LeaveCalendar } from '../components/LeaveCalendar.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select, Field, Textarea } from '../../../shell/Field.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDate } from '../../../lib/format.js';
import { downloadCsv } from '../../../lib/csv.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError, openApiContent } from '../../../lib/api.js';
import { useAuthStore } from '../../../store/auth.js';
import { TIME_OFF_TYPE, type TimeOffDuration, type TimeOffType, type TimeOffStatus } from '@shared/enums/index.js';
import type { TimeOffRequest } from '@shared/types';
import { inclusiveWeekdayCount } from '@shared/util/dates.js';

const statusTone: Record<TimeOffStatus, 'yellow' | 'green' | 'red'> = {
  Pending: 'yellow',
  Approved: 'green',
  Denied: 'red',
};

const WORKFORCE_PTO_MESSAGE =
  'You must have PTO available in Workforce, and you must also request these PTO days in Workforce to ensure payroll is correct.';

function timeOffTypeLabel(type: string): string {
  if (type === 'UTO') return 'UTO (Unpaid)';
  return type;
}

function timeOffDurationLabel(request: TimeOffRequest): string {
  if (request.duration === 'PartialDay' && request.startTime && request.endTime) {
    return `${request.startTime}–${request.endTime}`;
  }
  return `${inclusiveWeekdayCount(request.from, request.to)} days`;
}

/** Popup + inline callout reminding requesters about Workforce. */
function WorkforcePtoNotice({
  open,
  onAcknowledge,
}: {
  open: boolean;
  onAcknowledge: () => void;
}) {
  return (
    <Modal open={open} onClose={onAcknowledge} title="Workforce PTO reminder" size="sm">
      <div className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-900 dark:text-amber-100">{WORKFORCE_PTO_MESSAGE}</p>
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={onAcknowledge}>
            I understand
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function WorkforcePtoBanner() {
  return (
    <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{WORKFORCE_PTO_MESSAGE}</span>
    </div>
  );
}

export function TimeOffPage() {
  const { data, isLoading } = useTimeOff();
  const { user } = useAuthStore();
  const { data: directory } = useEmployeeDirectory();
  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TimeOffRequest | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [focusedDay, setFocusedDay] = useState<string | null>(null);
  const decide = useDecideTimeOff();
  const deleteTimeOff = useDeleteTimeOff();

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this time-off request? This cannot be undone.')) return;
    try {
      await deleteTimeOff.mutateAsync(id);
      toast('success', 'Time-off request deleted.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete.';
      toast('error', msg);
    }
  };

  const canApprove = user?.role === 'Admin' || user?.role === 'Manager';

  const handleDecide = async (id: string, status: 'Approved' | 'Denied') => {
    try {
      await decide.mutateAsync({ id, status });
      toast('success', `Request ${status.toLowerCase()}.`);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to update request.';
      toast('error', msg);
    }
  };

  const handleExport = () => {
    const rows = (data?.items ?? []).map((t) => ({
      employee: employeeLabel.get(t.employeeId) ?? t.employeeId,
      type: timeOffTypeLabel(t.type),
      from: t.from.slice(0, 10),
      to: t.to.slice(0, 10),
      duration: t.duration === 'PartialDay' ? 'Partial day' : 'Full day',
      startTime: t.startTime ?? '',
      endTime: t.endTime ?? '',
      status: t.status,
      reason: t.reason ?? '',
      decidedAt: t.decidedAt?.slice(0, 10) ?? '',
    }));
    downloadCsv(`time-off-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast('success', 'CSV downloaded.');
  };

  const columns: Column<TimeOffRequest>[] = [
    {
      key: 'employee',
      header: 'Employee',
      render: (t) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {employeeLabel.get(t.employeeId) ?? 'Employee'}
        </span>
      ),
    },
    { key: 'type', header: 'Type', render: (t) => timeOffTypeLabel(t.type) },
    { key: 'from', header: 'From', render: (t) => formatDate(t.from) },
    { key: 'to', header: 'To', render: (t) => formatDate(t.to) },
    {
      key: 'reason',
      header: 'Reason',
      render: (t) => (
        <span className="block max-w-[14rem] truncate text-sm text-slate-600 dark:text-slate-400" title={t.reason ?? undefined}>
          {t.reason ?? '—'}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: timeOffDurationLabel,
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Badge tone={statusTone[t.status]}>{t.status}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      render: (t) => {
        const canEditPending = t.status === 'Pending' && (canApprove || t.employeeId === user?.employeeId);
        const canDelete =
          (t.status === 'Pending' && (canApprove || t.employeeId === user?.employeeId)) ||
          (user?.role === 'Admin' && (t.status === 'Approved' || t.status === 'Denied'));
        const canDownloadPdf = t.status === 'Approved' || t.status === 'Denied';
        if (!canEditPending && !(canApprove && t.status === 'Pending') && !canDownloadPdf && !canDelete) return null;
        return (
          <div className="flex justify-end gap-1">
            {canDownloadPdf && (
              <Button
                size="sm"
                variant="ghost"
                aria-label="Download PDF"
                onClick={(event) => {
                  event.stopPropagation();
                  void openApiContent(`/api/action-pdfs/time_off/${t.id}`).catch((error: unknown) => {
                    toast('error', error instanceof Error ? error.message : 'Failed to open PDF.');
                  });
                }}
              >
                <FileDown className="h-4 w-4" />
              </Button>
            )}
            {canEditPending && (
              <Button
                size="sm"
                variant="ghost"
                aria-label="Edit request"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditTarget(t);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="ghost"
                aria-label="Delete request"
                loading={deleteTimeOff.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(t.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {canApprove && t.status === 'Pending' && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={decide.isPending}
                  aria-label="Approve"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDecide(t.id, 'Approved');
                  }}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={decide.isPending}
                  aria-label="Deny"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDecide(t.id, 'Denied');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const tableItems = useMemo(() => {
    const items = data?.items ?? [];
    if (!focusedDay) return items;
    return items.filter(
      (t) =>
        (t.status === 'Approved' || t.status === 'Pending') &&
        t.from.slice(0, 10) <= focusedDay &&
        t.to.slice(0, 10) >= focusedDay,
    );
  }, [data?.items, focusedDay]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Time off</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {data?.items.length ?? 0} requests
            {focusedDay ? ` · filtered to ${focusedDay}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={!data?.items.length}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4" /> Request Time off
          </Button>
        </div>
      </div>

      {(data?.items.length ?? 0) > 0 && (
        <div
          className="inline-flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-700"
          role="tablist"
          aria-label="Time off view"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'list'}
            className={
              viewMode === 'list'
                ? 'rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900'
                : 'rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
            }
            onClick={() => setViewMode('list')}
          >
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'calendar'}
            className={
              viewMode === 'calendar'
                ? 'rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900'
                : 'rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
            }
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </button>
        </div>
      )}

      {viewMode === 'calendar' && !isLoading && (data?.items.length ?? 0) > 0 && (
        <LeaveCalendar
          requests={data?.items ?? []}
          employeeLabel={employeeLabel}
          focusedDay={focusedDay}
          onFocusDay={(day) => {
            setFocusedDay(day);
          }}
          onSelectRequest={(req) => {
            if (req.status === 'Pending' && (canApprove || req.employeeId === user?.employeeId)) {
              setEditTarget(req);
            } else {
              setFocusedDay(req.from.slice(0, 10));
              setViewMode('list');
            }
          }}
        />
      )}

      {viewMode === 'calendar' && focusedDay && (
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/40">
          <span className="text-slate-600 dark:text-slate-300">
            Day focused: <strong>{focusedDay}</strong>
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setViewMode('list')}
          >
            View in list
          </Button>
        </div>
      )}

      {viewMode === 'list' && focusedDay && (
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/40">
          <span className="text-slate-600 dark:text-slate-300">
            Showing requests covering <strong>{focusedDay}</strong>
          </span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setViewMode('calendar')}>
              Back to calendar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFocusedDay(null)}>
              Clear day filter
            </Button>
          </div>
        </div>
      )}

      {viewMode === 'list' &&
        (isLoading ? (
          <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : tableItems.length > 0 ? (
          <Table columns={columns} data={tableItems} />
        ) : (
          <EmptyState
            icon={<CalendarClock className="h-8 w-8" />}
            title={focusedDay ? 'No leave on this day' : 'No time-off requests'}
            description={
              focusedDay
                ? 'Pick another day on the calendar, or clear the filter.'
                : 'Submit a request to get started.'
            }
          />
        ))}

      {viewMode === 'calendar' && isLoading && (
        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      )}

      {viewMode === 'calendar' && !isLoading && (data?.items.length ?? 0) === 0 && (
        <EmptyState
          icon={<CalendarClock className="h-8 w-8" />}
          title="No time-off requests"
          description="Submit a request to get started."
        />
      )}

      <RequestModal open={modalOpen} onClose={() => setModalOpen(false)} />
      {editTarget && (
        <EditRequestModal request={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
}

function RequestModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateTimeOff();
  const { user } = useAuthStore();
  const [employeeId, setEmployeeId] = useState(user?.employeeId ?? '');
  const [type, setType] = useState<TimeOffType>('PTO');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [duration, setDuration] = useState<TimeOffDuration>('FullDay');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [workforceNoticeOpen, setWorkforceNoticeOpen] = useState(false);
  const [workforceAcknowledged, setWorkforceAcknowledged] = useState(false);

  // The modal mounts with the page, which can render before the auth store is
  // populated (e.g. a hard load of /time-off). Fill in the employee once known.
  useEffect(() => {
    if (user?.employeeId && !employeeId) setEmployeeId(user.employeeId);
  }, [user?.employeeId]); // eslint-disable-line react-hooks/exhaustive-deps -- only when identity resolves

  const handleClose = () => {
    setFrom('');
    setTo('');
    setReason('');
    setType('PTO');
    setWorkforceNoticeOpen(false);
    setDuration('FullDay');
    setStartTime('');
    setEndTime('');
    setWorkforceAcknowledged(false);
    onClose();
  };

  const handleTypeChange = (next: TimeOffType) => {
    setType(next);
    if (next === 'PTO') {
      setWorkforceAcknowledged(false);
      setWorkforceNoticeOpen(true);
    }
  };

  const handleDurationChange = (next: TimeOffDuration) => {
    setDuration(next);
    if (next === 'PartialDay') {
      setTo(from);
    } else {
      setStartTime('');
      setEndTime('');
    }
  };

  const handleSubmit = async () => {
    if (type === 'PTO' && !workforceAcknowledged) {
      setWorkforceNoticeOpen(true);
      return;
    }
    try {
      await create.mutateAsync({
        employeeId,
        type,
        from: new Date(`${from}T00:00:00.000Z`).toISOString(),
        to: new Date(`${duration === 'PartialDay' ? from : to}T00:00:00.000Z`).toISOString(),
        duration,
        startTime: duration === 'PartialDay' ? startTime : null,
        endTime: duration === 'PartialDay' ? endTime : null,
        reason: reason.trim() || undefined,
      });
      toast('success', 'Request submitted.');
      handleClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to submit request.';
      toast('error', msg);
    }
  };

  // Default type is PTO — show the Workforce reminder when the modal opens.
  useEffect(() => {
    if (!open) {
      setWorkforceNoticeOpen(false);
      setWorkforceAcknowledged(false);
      return;
    }
    if (type === 'PTO' && !workforceAcknowledged) {
      setWorkforceNoticeOpen(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- only on open

  return (
    <>
      <Modal open={open} onClose={handleClose} title="Request Time off">
        <div className="space-y-4">
          <EmployeeSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            disabled={!!user?.employeeId && user.role === 'Employee'}
          />
          <Select
            label="Type"
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as TimeOffType)}
          >
            {TIME_OFF_TYPE.map((t) => (
              <option key={t} value={t}>{timeOffTypeLabel(t)}</option>
            ))}
          </Select>
          <Select
            label="Duration"
            value={duration}
            onChange={(e) => handleDurationChange(e.target.value as TimeOffDuration)}
          >
            <option value="FullDay">Full day</option>
            <option value="PartialDay">Partial day</option>
          </Select>
          {type === 'PTO' && <WorkforcePtoBanner />}
          {duration === 'PartialDay' ? (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Date">
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setTo(e.target.value);
                  }}
                />
              </Field>
              <Field label="Start time">
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </Field>
              <Field label="End time">
                <Input type="time" value={endTime} min={startTime || undefined} onChange={(e) => setEndTime(e.target.value)} />
              </Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="From">
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label="To">
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </div>
          )}
          <Field label="Reason">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Brief reason for this time-off request"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSubmit}
              loading={create.isPending}
              disabled={!employeeId || !from || (duration === 'FullDay' ? !to : !startTime || !endTime)}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>
      <WorkforcePtoNotice
        open={open && workforceNoticeOpen}
        onAcknowledge={() => {
          setWorkforceAcknowledged(true);
          setWorkforceNoticeOpen(false);
        }}
      />
    </>
  );
}

function EditRequestModal({
  request,
  onClose,
}: {
  request: TimeOffRequest;
  onClose: () => void;
}) {
  const update = useUpdateTimeOff();
  const initialType: TimeOffType =
    request.type === 'PTO' || request.type === 'UTO' ? request.type : 'PTO';
  const [type, setType] = useState<TimeOffType>(initialType);
  const [from, setFrom] = useState(request.from.slice(0, 10));
  const [to, setTo] = useState(request.to.slice(0, 10));
  const [duration, setDuration] = useState<TimeOffDuration>(request.duration);
  const [startTime, setStartTime] = useState(request.startTime ?? '');
  const [endTime, setEndTime] = useState(request.endTime ?? '');
  const [reason, setReason] = useState(request.reason ?? '');
  const [workforceNoticeOpen, setWorkforceNoticeOpen] = useState(false);
  const [workforceAcknowledged, setWorkforceAcknowledged] = useState(initialType !== 'PTO');

  useEffect(() => {
    if (initialType === 'PTO') {
      setWorkforceNoticeOpen(true);
      setWorkforceAcknowledged(false);
    }
  }, [request.id, initialType]);

  const handleTypeChange = (next: TimeOffType) => {
    setType(next);
    if (next === 'PTO') {
      setWorkforceAcknowledged(false);
      setWorkforceNoticeOpen(true);
    }
  };

  const handleDurationChange = (next: TimeOffDuration) => {
    setDuration(next);
    if (next === 'PartialDay') {
      setTo(from);
    } else {
      setStartTime('');
      setEndTime('');
    }
  };

  const handleSubmit = async () => {
    if (type === 'PTO' && !workforceAcknowledged) {
      setWorkforceNoticeOpen(true);
      return;
    }
    try {
      await update.mutateAsync({
        id: request.id,
        body: {
          type,
          from: new Date(`${from}T00:00:00.000Z`).toISOString(),
          to: new Date(`${duration === 'PartialDay' ? from : to}T00:00:00.000Z`).toISOString(),
          duration,
          startTime: duration === 'PartialDay' ? startTime : null,
          endTime: duration === 'PartialDay' ? endTime : null,
          reason: reason.trim() || undefined,
        },
      });
      toast('success', 'Request updated.');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to update request.';
      toast('error', msg);
    }
  };

  return (
    <>
      <Modal open onClose={onClose} title="Edit Time off Request">
        <div className="space-y-4">
          <Select
            label="Type"
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as TimeOffType)}
          >
            {TIME_OFF_TYPE.map((t) => (
              <option key={t} value={t}>{timeOffTypeLabel(t)}</option>
            ))}
          </Select>
          <Select
            label="Duration"
            value={duration}
            onChange={(e) => handleDurationChange(e.target.value as TimeOffDuration)}
          >
            <option value="FullDay">Full day</option>
            <option value="PartialDay">Partial day</option>
          </Select>
          {type === 'PTO' && <WorkforcePtoBanner />}
          {duration === 'PartialDay' ? (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Date">
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setTo(e.target.value);
                  }}
                />
              </Field>
              <Field label="Start time">
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </Field>
              <Field label="End time">
                <Input type="time" value={endTime} min={startTime || undefined} onChange={(e) => setEndTime(e.target.value)} />
              </Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="From">
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label="To">
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </div>
          )}
          <Field label="Reason">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Brief reason for this time-off request"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button type="button" onClick={handleSubmit} loading={update.isPending} disabled={!from || (duration === 'FullDay' ? !to : !startTime || !endTime)}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
      <WorkforcePtoNotice
        open={workforceNoticeOpen}
        onAcknowledge={() => {
          setWorkforceAcknowledged(true);
          setWorkforceNoticeOpen(false);
        }}
      />
    </>
  );
}
